<template>
  <div class="max-w-md mx-auto px-4 pt-5 pb-20">
    <MePlaceholder v-if="isLoading"/>

    <template v-else>
      <div
        style="min-height: 144px"
        class="flex flex-col justify-center items-center mb-6">
        <router-link
          to="/me/edit"
          class="mb-4 relative inline-block">
          <MeAvatar class="w-20 h-20"/>

          <div class="absolute -bottom-1.5 left-0 right-0 flex justify-center">
            <div class="flex items-center bg-[#2acf81] text-caption text-white rounded px-1.5 py-0.5">
              online

              <InlineSvg
                v-if="isMobile"
                class="inline-block shrink-0 w-3.5 ml-0.5"
                :src="require('@/assets/svg/design/i_mobile.svg')"/>

              <InlineSvg
                v-else
                class="inline-block shrink-0 w-3.5 ml-0.5"
                :src="require('@/assets/svg/design/i_desktop.svg')"/>
            </div>
          </div>
        </router-link>

        <h1 class="h3 mb-1">{{ user.name }}</h1>

        <div class="flex items-center gap-2">
          <span
            v-if="user.username"
            class="font-bold text-gray-500">@{{ user.username }}</span>

          <RatingsBadge
            v-if="user.rankingPositions"
            class="my-1 whitespace-nowrap max-w-max"
            :is-newbie="user.isNewbie"
            :ranking-positions="user.rankingPositions"/>
        </div>
      </div>

      <div
        v-if="user.interests"
        class="flex items-center mx-auto flex-wrap justify-center gap-2 text-[18px] mb-5 -mt-3 max-w-xs">
        <span
          v-for="item in user.interests.split(',')"
          :key="item"
          :title="item">
          {{ getInterestWithEmoji(item) }}
        </span>
      </div>

      <MeProfileCompletion
        v-if="options.profileCompletenessEnabled"
        class="my-3.5"/>

      <AppEnterYourEmailBanner
        v-if="!user.email && isAuthenticated"
        class="mb-4"/>

      <AppOpenInBrowserBanner
        v-if="showOpenInBrowserBanner"
        class="mt-2.5 mb-3"/>

      <router-link
        to="/credits"
        :class="[showAppealPWA ? 'mb-4' : 'mb-10']"
        class="block">
        <div class="balance-section flex items-center justify-between px-5 py-4">
          <div>
            <div class="font-bold text-lg text-white mb-1 flex items-center">
              {{ $t('user.me.balance') }}: {{ balance }}

              <InlineSvg
                class="h-4 w-4 ml-1.5"
                :src="require('@/assets/svg/design/coin.svg')"/>
            </div>

            <div class="text-white">
              {{ $t('user.me.add_funds_to_wallet') }}
            </div>
          </div>

          <div>
            <svg
              width="13"
              height="21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M2.963 20.083l9.245-9.137a.975.975 0 0 0 0-1.392L2.963.417a1.456 1.456 0 0 0-2.04 0 1.415 1.415 0 0 0 0 2.016l7.908 7.818-7.909 7.815a1.417 1.417 0 0 0 0 2.017 1.456 1.456 0 0 0 2.04 0z"
                fill="#fff"/>
            </svg>
          </div>
        </div>
      </router-link>

      <!--v-if="showAppealPWA"-->
      <BannerPWA class="mb-10"/>

      <ul class="space-y-4">
        <li>
          <router-link
            class="flex justify-between hover:text-link"
            to="/me/edit">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="14"
                  height="17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M11.42 4.948c0 2.457-1.98 4.448-4.42 4.448S2.58 7.405 2.58 4.948C2.58 2.491 4.56.5 7 .5s4.42 1.991 4.42 4.448zm-1.538 0A2.892 2.892 0 0 1 7 7.85a2.892 2.892 0 0 1-2.882-2.9A2.892 2.892 0 0 1 7 2.046a2.892 2.892 0 0 1 2.882 2.901z"
                    fill="#86878B"/>
                  <path
                    d="M.016 15.757a.373.373 0 0 0 .233.457l.717.263a.407.407 0 0 0 .522-.275c.724-2.373 2.918-4.098 5.512-4.098 2.594 0 4.788 1.725 5.511 4.098a.407.407 0 0 0 .523.274l.717-.262a.373.373 0 0 0 .233-.457c-.913-3.01-3.694-5.2-6.984-5.2s-6.071 2.19-6.984 5.2z"
                    fill="#86878B"/>
                </svg>
              </div>

              <div class="flex-1">
                {{ $t('user.me.edit_profile') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </router-link>
        </li>

        <li v-if="purchaseConfig?.currentPaymentMethodId">
          <router-link
            class="flex justify-between hover:text-link"
            to="/saved-cards">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="17"
                  height="17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M.75 2.75a2 2 0 0 1 2-2H13a2 2 0 0 1 2 2v1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H2.75a2 2 0 0 1-2-2v-12zm12.75 1v-1a.5.5 0 0 0-.5-.5H2.75a.5.5 0 0 0-.5.5v1H13.5zM2.25 5.25v9.5a.5.5 0 0 0 .5.5H15a.5.5 0 0 0 .5-.5v-2H13a2.5 2.5 0 0 1 0-5h2.5v-2a.5.5 0 0 0-.5-.5H2.25zm13.25 4H13a1 1 0 1 0 0 2h2.5v-2z"
                    fill="#86878B"/>
                </svg>
              </div>

              <div class="flex-1">
                {{ $t('user.me.your_cards') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </router-link>
        </li>

        <li v-if="['stripe'].includes(paymentProcessor)">
          <a
            class="flex justify-between items-center hover:text-link cursor-pointer"
            @click="checkAddingCard">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="17"
                  height="17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M.75 2.75a2 2 0 0 1 2-2H13a2 2 0 0 1 2 2v1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H2.75a2 2 0 0 1-2-2v-12zm12.75 1v-1a.5.5 0 0 0-.5-.5H2.75a.5.5 0 0 0-.5.5v1H13.5zM2.25 5.25v9.5a.5.5 0 0 0 .5.5H15a.5.5 0 0 0 .5-.5v-2H13a2.5 2.5 0 0 1 0-5h2.5v-2a.5.5 0 0 0-.5-.5H2.25zm13.25 4H13a1 1 0 1 0 0 2h2.5v-2z"
                    fill="#86878B"/>
                </svg>
              </div>

              <span
                v-if="trialCreditsForBankCard?.creditCount && !trialCreditsForBankCard?.credited"
                class="whitespace-nowrap flex items-center gap-1 flex-wrap">
                {{ $t('user.me.add_card_and_get') }}

                <span class="font-semibold text-white gift-size">
                  {{
                    $t('user.me.free_credits', { amount: trialCreditsForBankCard.creditCount })
                  }}
                </span>
              </span>

              <span v-else>
                {{ $t('user.me.add_card') }}
              </span>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <router-link
            class="flex justify-between hover:text-link"
            to="/subscriptions">
            <div class="flex items-center">
              <div class="w-7">
                <ListIcon class="w-4 text-gray-500"/>
              </div>

              <div class="flex-1">
                {{ isDatingEnabled ? $t('user.me.premium_subscription') : $t('user.me.subscriptions') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </router-link>
        </li>

        <li v-if="!isDatingEnabled">
          <router-link
            class="flex justify-between hover:text-link"
            to="/payments">
            <div class="flex items-center">
              <div class="w-7">
                <InlineSvg :src="require('@/assets/svg/design/i_payments.svg')"/>
              </div>

              <div class="flex-1">
                {{ $t('user.me.payments') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </router-link>
        </li>

        <li>
          <a
            class="flex justify-between hover:text-link"
            target="_blank"
            :href="`${$window.one2fan.origin}/how`">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <mask
                    id="mask0"
                    mask-type="alpha"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="17"
                    height="17">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M11.0312 0C12.7099 0 13.549 0 14.4527 0.290417C15.4392 0.655492 16.2162 1.44543 16.5753 2.44843C16.861 3.36699 16.861 4.22039 16.861 5.92691V11.0731C16.861 12.7798 16.861 13.6329 16.5753 14.5516C16.2162 15.5546 15.4392 16.3445 14.4527 16.7094C13.549 17 12.7099 17 11.0312 17H5.9694C4.29071 17 3.45144 17 2.54794 16.7094C1.56138 16.3445 0.784394 15.5546 0.425304 14.5516C0.139648 13.6329 0.139648 12.7798 0.139648 11.0731V5.92691C0.139648 4.22039 0.139648 3.36699 0.425304 2.44843C0.784394 1.44543 1.56138 0.655492 2.54794 0.290417C3.45144 0 4.29071 0 5.9694 0H11.0312Z"
                      fill="white"/>
                  </mask>
                  <g mask="url(#mask0)">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M11.0312 0C12.7099 0 13.549 0 14.4527 0.290417C15.4392 0.655492 16.2162 1.44543 16.5753 2.44843C16.861 3.36699 16.861 4.22039 16.861 5.92691V11.0731C16.861 12.7798 16.861 13.6329 16.5753 14.5516C16.2162 15.5546 15.4392 16.3445 14.4527 16.7094C13.549 17 12.7099 17 11.0312 17H5.9694C4.29071 17 3.45144 17 2.54794 16.7094C1.56138 16.3445 0.784394 15.5546 0.425304 14.5516C0.139648 13.6329 0.139648 12.7798 0.139648 11.0731V5.92691C0.139648 4.22039 0.139648 3.36699 0.425304 2.44843C0.784394 1.44543 1.56138 0.655492 2.54794 0.290417C3.45144 0 4.29071 0 5.9694 0H11.0312Z"
                      fill="#8d8f92"/>
                    <path
                      d="M10.8373 10.0827C11.651 10.0827 12.3364 10.7667 12.025 11.5185V11.5185C11.8365 11.9737 11.5601 12.3872 11.2117 12.7356C10.8633 13.084 10.4498 13.3603 9.99458 13.5489C9.5394 13.7374 9.05154 13.8345 8.55886 13.8345C8.06617 13.8345 7.57831 13.7374 7.12313 13.5489C6.66795 13.3603 6.25436 13.084 5.90598 12.7356C5.5576 12.3872 5.28125 11.9737 5.09271 11.5185V11.5185C4.78133 10.7667 5.46674 10.0827 6.28042 10.0827L8.55886 10.0827H10.8373Z"
                      fill="white"/>
                    <path
                      d="M4.66868 5.25913C4.82284 4.78466 5.49409 4.78466 5.64826 5.25913V5.25913C5.7172 5.47132 5.91494 5.61498 6.13805 5.61498V5.61498C6.63693 5.61498 6.84436 6.25338 6.44075 6.54662V6.54662C6.26025 6.67776 6.18472 6.91021 6.25367 7.1224V7.1224C6.40783 7.59687 5.86478 7.99142 5.46117 7.69818V7.69818C5.28067 7.56704 5.03626 7.56704 4.85576 7.69818V7.69818C4.45215 7.99142 3.9091 7.59687 4.06326 7.1224V7.1224C4.13221 6.91021 4.05668 6.67776 3.87618 6.54662V6.54662C3.47257 6.25338 3.68 5.61498 4.17889 5.61498V5.61498C4.402 5.61498 4.59973 5.47132 4.66868 5.25913V5.25913Z"
                      fill="white"/>
                    <path
                      d="M11.469 5.25913C11.6231 4.78466 12.2944 4.78466 12.4485 5.25913V5.25913C12.5175 5.47132 12.7152 5.61498 12.9383 5.61498V5.61498C13.4372 5.61498 13.6447 6.25338 13.241 6.54662V6.54662C13.0605 6.67776 12.985 6.91021 13.054 7.1224V7.1224C13.2081 7.59687 12.6651 7.99142 12.2615 7.69818V7.69818C12.081 7.56704 11.8366 7.56704 11.6561 7.69818V7.69818C11.2524 7.99142 10.7094 7.59687 10.8636 7.1224V7.1224C10.9325 6.91021 10.857 6.67776 10.6765 6.54662V6.54662C10.2729 6.25338 10.4803 5.61498 10.9792 5.61498V5.61498C11.2023 5.61498 11.4 5.47132 11.469 5.25913V5.25913Z"
                      fill="white"/>
                  </g>
                </svg>
              </div>

              <div class="flex-1">
                {{ $t('user.me.become_a_creator') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            class="flex justify-between hover:text-link cursor-pointer"
            @click="$refs.langSwitcher.openModal()">
            <div class="flex items-center">
              <div class="w-7">
                <InlineSvg :src="require('@/assets/svg/design/union.svg')"/>
              </div>

              <div class="flex-1">
                {{ $t('common.app_language') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li v-if="options.messageTranslationEnabled">
          <a
            class="flex justify-between hover:text-link cursor-pointer"
            @click="openChatLangSwitcherModal">
            <div class="flex items-center">
              <div class="w-5 mr-2">
                <InlineSvg :src="require('@/assets/svg/design/translate.svg')"/>
              </div>

              <div class="flex-1">
                {{ $t('translate.translations') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            :href="supportLink"
            class="flex justify-between hover:text-link -ml-1">
            <div class="flex items-center">
              <InlineSvg
                class="inline-block w-6 mr-2"
                :src="require('@/assets/svg/design/me/support.svg')"/>

              <div class="flex-1">
                {{ $t('user.me.support') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            target="_blank"
            class="flex justify-between hover:text-link cursor-pointer -ml-1"
            :href="`${$window.one2fan.origin}/contact-us`">
            <div class="flex items-center">
              <InlineSvg
                class="inline-block w-6 mr-2"
                :src="require('@/assets/svg/design/me/contact_us.svg')"/>

              <div class="flex-1">
                {{ $t('common.contact_us') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            target="_blank"
            class="flex justify-between hover:text-link cursor-pointer -ml-1"
            href="https://one2fan.com/blog">
            <div class="flex items-center">
              <InlineSvg
                class="inline-block p-0.5 w-6 mr-2 text-[#86878B] stroke-[#86878B]"
                :src="require('@/assets/svg/design/one2fan-blog.svg')"/>

              <div class="flex-1">
                One2fan Blog
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            class="flex justify-between hover:text-link"
            target="_blank"
            :href="`${$window.one2fan.origin}/terms`">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="17"
                  height="19"
                  viewBox="0 0 17 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M3 2H14C14.5523 2 15 2.44772 15 3V16C15 16.5523 14.5523 17 14 17H3C2.44772 17 2 16.5523 2 16V3C2 2.44772 2.44772 2 3 2ZM1 3C1 1.89543 1.89543 1 3 1H14C15.1046 1 16 1.89543 16 3V16C16 17.1046 15.1046 18 14 18H3C1.89543 18 1 17.1046 1 16V3ZM4.75 10H12.25C12.5087 10 12.7188 9.776 12.7188 9.5C12.7188 9.224 12.5087 9 12.25 9H4.75C4.49125 9 4.28125 9.224 4.28125 9.5C4.28125 9.776 4.49125 10 4.75 10ZM12.25 13H4.75C4.49125 13 4.28125 12.776 4.28125 12.5C4.28125 12.224 4.49125 12 4.75 12H12.25C12.5087 12 12.7188 12.224 12.7188 12.5C12.7188 12.776 12.5087 13 12.25 13ZM4.75 7H12.25C12.5087 7 12.7188 6.776 12.7188 6.5C12.7188 6.224 12.5087 6 12.25 6H4.75C4.49125 6 4.28125 6.224 4.28125 6.5C4.28125 6.776 4.49125 7 4.75 7Z"
                    fill="#86878B"/>
                  <path
                    d="M14 1.75H3V2.25H14V1.75ZM15.25 3C15.25 2.30964 14.6904 1.75 14 1.75V2.25C14.4142 2.25 14.75 2.58579 14.75 3H15.25ZM15.25 16V3H14.75V16H15.25ZM14 17.25C14.6904 17.25 15.25 16.6904 15.25 16H14.75C14.75 16.4142 14.4142 16.75 14 16.75V17.25ZM3 17.25H14V16.75H3V17.25ZM1.75 16C1.75 16.6904 2.30964 17.25 3 17.25V16.75C2.58579 16.75 2.25 16.4142 2.25 16H1.75ZM1.75 3V16H2.25V3H1.75ZM3 1.75C2.30964 1.75 1.75 2.30964 1.75 3H2.25C2.25 2.58579 2.58579 2.25 3 2.25V1.75ZM3 0.75C1.75736 0.75 0.75 1.75736 0.75 3H1.25C1.25 2.0335 2.0335 1.25 3 1.25V0.75ZM14 0.75H3V1.25H14V0.75ZM16.25 3C16.25 1.75736 15.2426 0.75 14 0.75V1.25C14.9665 1.25 15.75 2.0335 15.75 3H16.25ZM16.25 16V3H15.75V16H16.25ZM14 18.25C15.2426 18.25 16.25 17.2426 16.25 16H15.75C15.75 16.9665 14.9665 17.75 14 17.75V18.25ZM3 18.25H14V17.75H3V18.25ZM0.75 16C0.75 17.2426 1.75736 18.25 3 18.25V17.75C2.0335 17.75 1.25 16.9665 1.25 16H0.75ZM0.75 3V16H1.25V3H0.75ZM12.25 9.75H4.75V10.25H12.25V9.75ZM12.4688 9.5C12.4688 9.65337 12.3557 9.75 12.25 9.75V10.25C12.6618 10.25 12.9688 9.89863 12.9688 9.5H12.4688ZM12.25 9.25C12.3557 9.25 12.4688 9.34663 12.4688 9.5H12.9688C12.9688 9.10137 12.6618 8.75 12.25 8.75V9.25ZM4.75 9.25H12.25V8.75H4.75V9.25ZM4.53125 9.5C4.53125 9.34663 4.64427 9.25 4.75 9.25V8.75C4.33823 8.75 4.03125 9.10137 4.03125 9.5H4.53125ZM4.75 9.75C4.64427 9.75 4.53125 9.65337 4.53125 9.5H4.03125C4.03125 9.89863 4.33823 10.25 4.75 10.25V9.75ZM4.75 13.25H12.25V12.75H4.75V13.25ZM4.03125 12.5C4.03125 12.8986 4.33823 13.25 4.75 13.25V12.75C4.64427 12.75 4.53125 12.6534 4.53125 12.5H4.03125ZM4.75 11.75C4.33823 11.75 4.03125 12.1014 4.03125 12.5H4.53125C4.53125 12.3466 4.64427 12.25 4.75 12.25V11.75ZM12.25 11.75H4.75V12.25H12.25V11.75ZM12.9688 12.5C12.9688 12.1014 12.6618 11.75 12.25 11.75V12.25C12.3557 12.25 12.4688 12.3466 12.4688 12.5H12.9688ZM12.25 13.25C12.6618 13.25 12.9688 12.8986 12.9688 12.5H12.4688C12.4688 12.6534 12.3557 12.75 12.25 12.75V13.25ZM12.25 6.75H4.75V7.25H12.25V6.75ZM12.4688 6.5C12.4688 6.65337 12.3557 6.75 12.25 6.75V7.25C12.6618 7.25 12.9688 6.89863 12.9688 6.5H12.4688ZM12.25 6.25C12.3557 6.25 12.4688 6.34663 12.4688 6.5H12.9688C12.9688 6.10137 12.6618 5.75 12.25 5.75V6.25ZM4.75 6.25H12.25V5.75H4.75V6.25ZM4.53125 6.5C4.53125 6.34663 4.64427 6.25 4.75 6.25V5.75C4.33823 5.75 4.03125 6.10137 4.03125 6.5H4.53125ZM4.75 6.75C4.64427 6.75 4.53125 6.65337 4.53125 6.5H4.03125C4.03125 6.89863 4.33823 7.25 4.75 7.25V6.75Z"
                    fill="#86878B"/>
                </svg>
              </div>

              <div class="flex-1">
                {{ $t('user.me.terms_of_service') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            class="flex justify-between hover:text-link"
            target="_blank"
            :href="`${$window.one2fan.origin}/policy`">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="16"
                  height="19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.75 12.8a1.5 1.5 0 1 0-1.5 0V15a.75.75 0 0 0 1.5 0v-2.2z"
                    fill="#86878B"/>
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M12.25 6.75V5a4.25 4.25 0 0 0-8.5 0v1.75H3.5A2.75 2.75 0 0 0 .75 9.5v6a2.75 2.75 0 0 0 2.75 2.75h9a2.75 2.75 0 0 0 2.75-2.75v-6a2.75 2.75 0 0 0-2.75-2.75h-.25zM8 2.25A2.75 2.75 0 0 0 5.25 5v1.75h5.5V5A2.75 2.75 0 0 0 8 2.25zm-4.5 6c-.69 0-1.25.56-1.25 1.25v6c0 .69.56 1.25 1.25 1.25h9c.69 0 1.25-.56 1.25-1.25v-6c0-.69-.56-1.25-1.25-1.25h-9z"
                    fill="#86878B"/>
                </svg>
              </div>

              <div class="flex-1">
                {{ $t('user.me.privacy_policy') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            class="flex justify-between hover:text-link"
            target="_blank"
            :href="`${$window.one2fan.origin}/ip-infringement-policy`">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="16"
                  height="19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.75 12.8a1.5 1.5 0 1 0-1.5 0V15a.75.75 0 0 0 1.5 0v-2.2z"
                    fill="#86878B"/>
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M12.25 6.75V5a4.25 4.25 0 0 0-8.5 0v1.75H3.5A2.75 2.75 0 0 0 .75 9.5v6a2.75 2.75 0 0 0 2.75 2.75h9a2.75 2.75 0 0 0 2.75-2.75v-6a2.75 2.75 0 0 0-2.75-2.75h-.25zM8 2.25A2.75 2.75 0 0 0 5.25 5v1.75h5.5V5A2.75 2.75 0 0 0 8 2.25zm-4.5 6c-.69 0-1.25.56-1.25 1.25v6c0 .69.56 1.25 1.25 1.25h9c.69 0 1.25-.56 1.25-1.25v-6c0-.69-.56-1.25-1.25-1.25h-9z"
                    fill="#86878B"/>
                </svg>
              </div>

              <div class="flex-1">
                {{ $t('user.me.ip_infringement_policy') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>

        <li>
          <a
            class="flex justify-between hover:text-link"
            target="_blank"
            :href="`${$window.one2fan.origin}/contract`">
            <div class="flex items-center">
              <div class="w-7">
                <svg
                  width="17"
                  height="19"
                  viewBox="0 0 17 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M3 2H14C14.5523 2 15 2.44772 15 3V16C15 16.5523 14.5523 17 14 17H3C2.44772 17 2 16.5523 2 16V3C2 2.44772 2.44772 2 3 2ZM1 3C1 1.89543 1.89543 1 3 1H14C15.1046 1 16 1.89543 16 3V16C16 17.1046 15.1046 18 14 18H3C1.89543 18 1 17.1046 1 16V3ZM4.75 10H12.25C12.5087 10 12.7188 9.776 12.7188 9.5C12.7188 9.224 12.5087 9 12.25 9H4.75C4.49125 9 4.28125 9.224 4.28125 9.5C4.28125 9.776 4.49125 10 4.75 10ZM12.25 13H4.75C4.49125 13 4.28125 12.776 4.28125 12.5C4.28125 12.224 4.49125 12 4.75 12H12.25C12.5087 12 12.7188 12.224 12.7188 12.5C12.7188 12.776 12.5087 13 12.25 13ZM4.75 7H12.25C12.5087 7 12.7188 6.776 12.7188 6.5C12.7188 6.224 12.5087 6 12.25 6H4.75C4.49125 6 4.28125 6.224 4.28125 6.5C4.28125 6.776 4.49125 7 4.75 7Z"
                    fill="#86878B"/>
                  <path
                    d="M14 1.75H3V2.25H14V1.75ZM15.25 3C15.25 2.30964 14.6904 1.75 14 1.75V2.25C14.4142 2.25 14.75 2.58579 14.75 3H15.25ZM15.25 16V3H14.75V16H15.25ZM14 17.25C14.6904 17.25 15.25 16.6904 15.25 16H14.75C14.75 16.4142 14.4142 16.75 14 16.75V17.25ZM3 17.25H14V16.75H3V17.25ZM1.75 16C1.75 16.6904 2.30964 17.25 3 17.25V16.75C2.58579 16.75 2.25 16.4142 2.25 16H1.75ZM1.75 3V16H2.25V3H1.75ZM3 1.75C2.30964 1.75 1.75 2.30964 1.75 3H2.25C2.25 2.58579 2.58579 2.25 3 2.25V1.75ZM3 0.75C1.75736 0.75 0.75 1.75736 0.75 3H1.25C1.25 2.0335 2.0335 1.25 3 1.25V0.75ZM14 0.75H3V1.25H14V0.75ZM16.25 3C16.25 1.75736 15.2426 0.75 14 0.75V1.25C14.9665 1.25 15.75 2.0335 15.75 3H16.25ZM16.25 16V3H15.75V16H16.25ZM14 18.25C15.2426 18.25 16.25 17.2426 16.25 16H15.75C15.75 16.9665 14.9665 17.75 14 17.75V18.25ZM3 18.25H14V17.75H3V18.25ZM0.75 16C0.75 17.2426 1.75736 18.25 3 18.25V17.75C2.0335 17.75 1.25 16.9665 1.25 16H0.75ZM0.75 3V16H1.25V3H0.75ZM12.25 9.75H4.75V10.25H12.25V9.75ZM12.4688 9.5C12.4688 9.65337 12.3557 9.75 12.25 9.75V10.25C12.6618 10.25 12.9688 9.89863 12.9688 9.5H12.4688ZM12.25 9.25C12.3557 9.25 12.4688 9.34663 12.4688 9.5H12.9688C12.9688 9.10137 12.6618 8.75 12.25 8.75V9.25ZM4.75 9.25H12.25V8.75H4.75V9.25ZM4.53125 9.5C4.53125 9.34663 4.64427 9.25 4.75 9.25V8.75C4.33823 8.75 4.03125 9.10137 4.03125 9.5H4.53125ZM4.75 9.75C4.64427 9.75 4.53125 9.65337 4.53125 9.5H4.03125C4.03125 9.89863 4.33823 10.25 4.75 10.25V9.75ZM4.75 13.25H12.25V12.75H4.75V13.25ZM4.03125 12.5C4.03125 12.8986 4.33823 13.25 4.75 13.25V12.75C4.64427 12.75 4.53125 12.6534 4.53125 12.5H4.03125ZM4.75 11.75C4.33823 11.75 4.03125 12.1014 4.03125 12.5H4.53125C4.53125 12.3466 4.64427 12.25 4.75 12.25V11.75ZM12.25 11.75H4.75V12.25H12.25V11.75ZM12.9688 12.5C12.9688 12.1014 12.6618 11.75 12.25 11.75V12.25C12.3557 12.25 12.4688 12.3466 12.4688 12.5H12.9688ZM12.25 13.25C12.6618 13.25 12.9688 12.8986 12.9688 12.5H12.4688C12.4688 12.6534 12.3557 12.75 12.25 12.75V13.25ZM12.25 6.75H4.75V7.25H12.25V6.75ZM12.4688 6.5C12.4688 6.65337 12.3557 6.75 12.25 6.75V7.25C12.6618 7.25 12.9688 6.89863 12.9688 6.5H12.4688ZM12.25 6.25C12.3557 6.25 12.4688 6.34663 12.4688 6.5H12.9688C12.9688 6.10137 12.6618 5.75 12.25 5.75V6.25ZM4.75 6.25H12.25V5.75H4.75V6.25ZM4.53125 6.5C4.53125 6.34663 4.64427 6.25 4.75 6.25V5.75C4.33823 5.75 4.03125 6.10137 4.03125 6.5H4.53125ZM4.75 6.75C4.64427 6.75 4.53125 6.65337 4.53125 6.5H4.03125C4.03125 6.89863 4.33823 7.25 4.75 7.25V6.75Z"
                    fill="#86878B"/>
                </svg>
              </div>

              <div class="flex-1">
                {{ $t('user.me.cnf_mutual_contract') }}
              </div>
            </div>

            <InlineSvg :src="require('@/assets/svg/design/arrow-right.svg')"/>
          </a>
        </li>
      </ul>

      <div class="flex justify-center items-center gap-4 my-4">
        <a
          href="https://twitter.com/one2fan_"
          class="text-[#bfbfbf] hover:text-gray-500"
          target="_blank">
          <InlineSvg
            class="w-6 h-6"
            :src="require('@/assets/fontawesome/brands/x-twitter.svg')"/>
        </a>

        <a
          href="https://www.tiktok.com/@one2.fan"
          class="text-[#bfbfbf] hover:text-gray-500"
          target="_blank">
          <InlineSvg
            class="w-6 h-6"
            :src="require('@/assets/fontawesome/brands/tiktok.svg')"/>
        </a>

        <a
          href="https://t.me/one2fan"
          class="text-[#bfbfbf] hover:text-gray-500"
          target="_blank">
          <InlineSvg
            class="w-6 h-6"
            :src="require('@/assets/fontawesome/brands/telegram.svg')"/>
        </a>

        <a
          v-if="0"
          href="#"
          class="text-[#bfbfbf] hover:text-gray-500"
          target="_blank">
          <InlineSvg
            class="w-6 h-6"
            :src="require('@/assets/fontawesome/brands/tiktok.svg')"/>
        </a>
      </div>

      <button
        class="btn btn-block btn-secondary"
        @click="openQuizBeforeLogout()">
        {{ $t('common.logout') }}
      </button>
    </template>
  </div>

  <Modal
    v-slot="{ closeModal }"
    ref="langSwitcher"
    position="bottom"
    overflow-class="overflow-y-auto"
    rounded-class="rounded-t-2.5xl"
    :has-indents="false"
    name="LangSwitcher">
    <LangSwitcherContent @close="closeModal()"/>
  </Modal>

  <Modal
    v-slot="{ closeModal }"
    ref="checkEmailNotifications"
    name="CheckEmailNotifications"
    :has-indents="true">
    <CheckEmailNotifications @close="closeModal"/>
  </Modal>

  <Navbar/>
</template>
<script>
import MeAvatar from '@/components/Me/Includes/MeAvatar.vue'
import ListIcon from '@/assets/svg-vue/ListIcon'
import { useMeta } from 'vue-meta'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { mapActions, mapState, mapWritableState } from 'pinia'
import BannerPWA from '@/components/Me/BannerPWA.vue'
import { useAppStore } from '@/stores/app'
import MePlaceholder from '@/components/Me/MePlaceholder'
import Navbar from '@/components/Navbar'
import LangSwitcherContent from '@/components/ModalContents/LangSwitcherContent'
import { usePaymentStore } from '@/stores/payment'
import { useQuizStore } from '@/stores/quiz'
import CheckEmailNotifications from '@/components/ModalContents/CheckEmailNotifications.vue'
import Modal from '@/components/Functional/Modal'
import MeProfileCompletion from '@/components/Me/Includes/MeProfileCompletion.vue'
import AppOpenInBrowserBanner from '@/components/App/AppOpenInBrowserBanner.vue'
import { isWebViewFullCheck } from '@/helpers/is-webview.js'
import { clearQueriesAndAddWebviewParams } from '@/helpers/app-helpers.js'
import RatingsBadge from '@/components/Ranking/Common/RankingBadge.vue'
import interests from '@/data/interests.json'
import AppEnterYourEmailBanner from '@/components/App/AppEnterYourEmailBanner.vue'
import ModalWindow from '@/components/Functional/ModalWindow.vue'
import AppEnterYourEmailModal from '@/components/App/AppEnterYourEmailModal.vue'
import { createComponent } from '@/helpers/utils.js'
import { openChatLangSwitcherModal } from '@/helpers/modals.js'

export default {
  name: 'Me',
  components: {
    MeAvatar,
    ListIcon,
    BannerPWA,
    MePlaceholder,
    Navbar,
    LangSwitcherContent,
    CheckEmailNotifications,
    Modal,
    MeProfileCompletion,
    AppOpenInBrowserBanner,
    RatingsBadge,
    AppEnterYourEmailBanner
  },
  setup () {
    useMeta({
      title: 'My profile',
      og: {
        title: 'My profile',
        description: 'One2fan – get closer to the creators',
        site_name: 'Fancy',
        type: 'website'
      }
    })
  },
  data () {
    return {
      isCardsLoaded: false,
      constantLoading: true
    }
  },
  computed: {
    ...mapState(useUserStore, [
      'user',
      'balance',
      'options',
      'premiumSupportAgentUserId'
    ]),
    ...mapState(useAppStore, [
      'showAppealPWA',
      'isDatingEnabled',
      'isMobile',
      'webViewSimulate'
    ]),
    ...mapState(useAuthStore, [
      'webViewEscape',
      'isAuthenticated'
    ]),
    ...mapWritableState(useAppStore, [
      'openDisableEmailNotificationsPopup'
    ]),
    ...mapState(usePaymentStore, [
      'purchaseConfig',
      'paymentProcessor',
      'trialCreditsForBankCard'
    ]),
    ...mapState(useQuizStore, [
      'quizAllowed'
    ]),
    ...mapWritableState(useQuizStore, [
      'surveyOpen',
      'surveyCause'
    ]),
    isLoading () {
      return !this.user.userId || this.constantLoading
    },
    supportLink () {
      return this.premiumSupportAgentUserId ? `/chat/${this.premiumSupportAgentUserId}` : 'mailto:support@one2fan.com'
    },
    showOpenInBrowserBanner () {
      return this.webViewEscape?.signinToken.value && (isWebViewFullCheck() || this.webViewSimulate)
    }
  },
  async created () {
    setTimeout(() => {
      this.constantLoading = false
    }, 500)

    await this.meGet()
  },
  mounted () {
    if (this.openDisableEmailNotificationsPopup) {
      this.$refs.checkEmailNotifications.openModal()
      this.openDisableEmailNotificationsPopup = false
    }

    clearQueriesAndAddWebviewParams()
  },
  methods: {
    ...mapActions(usePaymentStore, [
      'checkoutSessionCreate',
      'paymentMethodsGet'
    ]),
    ...mapActions(useAuthStore, [
      'logOut',
      'testMethod'
    ]),
    ...mapActions(useUserStore, [
      'meGet'
    ]),
    openChatLangSwitcherModal () {
      openChatLangSwitcherModal()
    },
    getInterestWithEmoji (interest) {
      return interests.find(item => {
        return item.toLowerCase().includes(interest.toLowerCase())
      }).split(' ')[0]
    },
    async addCard () {
      await this.checkoutSessionCreate({
        successUrl: `${window.origin}/checkout/result?is=success&type=add-card`,
        cancelUrl: `${window.origin}/checkout/result?is=cancel&type=add-card`,
        savePaymentMethod: true
      })
    },
    async checkAddingCard () {
      // Check email
      if (!this.user.email) {
        this.openAppEnterYourEmailModal({
          callback: this.addCard
        })
      }
      else {
        await this.addCard()
      }
    },
    openQuizBeforeLogout () {
      if (this.quizAllowed) {
        this.surveyOpen = true
        this.surveyCause = 'sign_out'
      }
      else {
        this.logOut()
      }
    },
    openAppEnterYourEmailModal (props = {}) {
      const modalProps = {
        wrapperClass: 'max-w-md m-auto w-full h-full flex mt-4'
      }

      createComponent(ModalWindow, modalProps, AppEnterYourEmailModal, props)
    }
  }
}
</script>

<style scoped>
  .balance-section {
    box-shadow: 0 4px 24px rgba(242, 52, 89, 0.5);
    background: linear-gradient(180deg, #F23459 0%, #C01855 100%);
    border-radius: 10px;
  }

  .gift-size {
    background: linear-gradient(270deg, #F23459 -8.94%, #C01855 144.79%);
    padding: 2px;
    padding-right: 20px;
    padding-left: 8px;
    clip-path: polygon(0% 0%, 100% 0, 93% 50%, 100% 100%, 0% 100%);
  }
</style>
